/**
 * router/routes/admin.routes.ts
 * Tenant-wide kernel/admin routes (user management, shared catalogs, company
 * profile, the client/contractor directory) nested under the tenant
 * AppShell — distinct from a module's OWN admin/config screens (e.g.
 * admin/locx/* lives in servicios.routes.ts).
 * Split out of router/index.ts (openspec/arquitectura/convencion-modulo.md §4).
 */
import type { RouteRecordRaw } from 'vue-router';

export const adminRoutes: RouteRecordRaw[] = [
  // FS-15: /admin/users — tenant admin user management (PR2 tenant-admin)
  // meta.requiresRole:'admin' — backend roleGuard is the authority; this gate
  // provides a cosmetic redirect to /dashboard for non-admin users (FS-13.2).
  {
    path: 'admin/users',
    name: 'admin-users',
    component: () => import('../../views/admin/UsersView.vue'),
    // add-app-shell-nav-redesign: admin screens are reached from the topbar's
    // Admin dropdown now, never the sidebar — no contextual nav to show here.
    meta: { requiresCapability: 'tenant.usuario.administrar', hideSidebar: true },
  },

  // permisos-por-capability stage 3 (AZ-27): the company's own roles and the
  // access change history — tabs next to Usuarios (AdminUsersTabs).
  {
    path: 'admin/roles',
    name: 'admin-roles',
    component: () => import('../../views/admin/RolesView.vue'),
    meta: { requiresCapability: 'tenant.usuario.administrar', hideSidebar: true },
  },
  {
    path: 'admin/access-audit',
    name: 'admin-access-audit',
    component: () => import('../../views/admin/AccessAuditView.vue'),
    meta: { requiresCapability: 'tenant.usuario.administrar', hideSidebar: true },
  },

  // /admin/catalogs — tenant-wide shared catalogs (cost center, delivery location).
  // hideSidebar removed 17 jul 2026 (SidebarRigx redesign): this route has no
  // module context of its own, so it used to carry its own in-page nav
  // (CatalogSidebarNav) as the only way to switch tabs — that's gone now
  // (CatalogsView.vue), the sidebar's "Administración" flyout stays mounted
  // and is the only nav, same as /rrhh/catalogs already worked.
  //
  // meta.requiresCapability (rediseno-panel-roles follow-up, 2026-09-15 user
  // report — bug found testing locx.supervisor): this route used to be
  // `requiresRole: 'admin'`, byte-unchanged since before SL-90 (locx-
  // supervisor-catalogo-nav) added PER-ITEM sidebar gating for "Centro de
  // costo"/"Clientes"/"Proveedores" (SidebarRigx.vue's `sharedItems`,
  // `tenant.centro-costo.editar`/`tenant.cliente.editar`/`sumi.proveedor.editar`)
  // — the sidebar link was reachable, but this router guard still only
  // matched the literal 'admin' role, so a locx.supervisor session (no
  // 'admin' role) got bounced straight back to /dashboard on click. Mirrors
  // `admin-locx-assets-config` below (requiresCapability alone, no
  // requiresRole) — 'admin' already satisfies any requiresCapability check
  // via its '*' wildcard (AZ-17.8), so this is a pure widening, nothing that
  // required 'admin' before stops working.
  {
    path: 'admin/catalogs',
    name: 'admin-catalogs',
    component: () => import('../../views/admin/CatalogsView.vue'),
    meta: {
      // requi.catalogo.editar (2026-10-13): Compras' own catalogs (artículos, rubros,
      // lugares de entrega, tipos de contrato) — the tabs are gated per item.
      // requi.stock.administrar / requi.oc.configurar-umbral (2026-10-14): Depósitos
      // and the OC threshold/terms tabs, for a purchasing manager who isn't admin.
      requiresCapability: [
        'tenant.centro-costo.editar',
        'tenant.cliente.editar',
        'sumi.proveedor.editar',
        'requi.catalogo.editar',
        'requi.stock.administrar',
        'requi.oc.configurar-umbral',
      ],
    },
  },

  // perfil-empresa-tenant TA2 — tenant-scoped "Datos de la empresa" (own
  // dedicated screen, same "own full screen, not a generic /admin/catalogs
  // tab" posture as 'clients'/'suppliers'/'contractors' below). Mirrors
  // GET/PATCH /admin/identity/company + logo upload (TA1), capability
  // tenant.empresa.editar.
  {
    path: 'admin/empresa',
    name: 'empresa-perfil',
    component: () => import('../../views/admin/EmpresaPerfilView.vue'),
    meta: { requiresCapability: 'tenant.empresa.editar' },
  },

  // add-kernel-client-enrichment — Cliente's own list + detail (contracts,
  // personas/activos afectados). GET /clients has no roleGuard (any
  // authenticated tenant user, same as the order-creation picker); writes
  // inside the detail view (PATCH/contracts) are admin-only at the backend.
  {
    path: 'clients',
    name: 'clients',
    component: () => import('../../views/admin/clients/ClientsListView.vue'),
  },

  {
    path: 'clients/:id',
    name: 'client-detail',
    component: () => import('../../views/admin/clients/ClientDetailView.vue'),
    props: true,
  },

  // add-sumi-proveedores-abm (22 ago 2026) — Proveedores own list+create+edit
  // screen, same "own full screen, not a generic catalog tab" posture as
  // 'clients' above. GET /sumi/suppliers requires compras:read (no admin
  // gate on read); PATCH is admin-only at the backend (roleGuard('admin')).
  // This is a SUMI-owned screen (see sumi.routes.ts's note) kept here to
  // preserve its exact original tie-break position relative to the other
  // single-segment routes below.
  {
    path: 'suppliers',
    name: 'suppliers',
    component: () => import('../../views/admin/suppliers/SuppliersListView.vue'),
  },
  // The supplier ficha — datos, fiscal, rubros, retenciones, OCs and cuenta corriente.
  {
    path: 'suppliers/:id',
    name: 'supplier-ficha',
    component: () => import('../../views/admin/suppliers/SupplierFichaView.vue'),
    props: true,
  },

  // kernel-contractor-directory PR4 (design.md CD7) — "Mis Contratistas"
  // own list+search+register+request screen, same "own full screen, not
  // a generic catalog tab" posture as 'clients'/'suppliers' above. Every
  // route under this capability requires roleGuard('admin') at the
  // backend already (design.md "Directory Access Guard").
  {
    path: 'contractors',
    name: 'contractors',
    component: () => import('../../views/admin/contractors/ContractorsView.vue'),
    meta: { requiresCapability: 'tenant.vinculacion.administrar' },
  },

  // kernel-mis-clientes fix (12 sep 2026) — "Mis Clientes", the
  // `owner`-side twin of 'contractors' above over the SAME directory
  // (design.md CD8's deferred "second view over the same route" — no
  // backend work, same roleGuard('admin') gate).
  {
    path: 'my-clients',
    name: 'my-clients',
    component: () => import('../../views/admin/contractors/ClientsView.vue'),
    meta: { requiresCapability: 'tenant.vinculacion.administrar' },
  },
];
